/**
 * Auto-computed column statistics using simple-statistics.
 *
 * Provides type-detected statistics for datasets, enabling LLMs to understand
 * data distributions without manual aggregation requests.
 */
import { mean, median, quantile, standardDeviation } from 'simple-statistics';

import { inferColumnType } from '#core/lib/transforms/column-schema.ts';
import { ISO_DATE_REGEX_BASIC } from '#core/lib/utils/date.ts';

/**
 * Stats for numeric columns.
 *
 * When a numeric-classified column contains no real numbers (e.g. every
 * value was null, undefined, or NaN), the summary statistics are `null`
 * rather than fabricated zeros — reporting min/max/mean = 0 on an empty
 * column silently misleads the LLM into treating "no data" as "all
 * measurements were zero". Consumers must handle the null variant when
 * rendering.
 */
export interface NumericColumnStats {
  type: 'numeric';
  count: number;
  nullCount: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  median: number | null;
  stdev: number | null;
  p25: number | null;
  p75: number | null;
}

/** Stats for string columns */
export interface StringColumnStats {
  type: 'string';
  count: number;
  nullCount: number;
  distinctCount: number;
}

/** Stats for date columns */
export interface DateColumnStats {
  type: 'date';
  count: number;
  nullCount: number;
  min: string;
  max: string;
}

/** Stats for columns with mixed types */
export interface MixedColumnStats {
  type: 'mixed';
  count: number;
  nullCount: number;
}

export type ColumnStats = NumericColumnStats | StringColumnStats | DateColumnStats | MixedColumnStats;

/** Result of auto-stats computation */
export interface AutoStatsResult {
  rowCount: number;
  columns: Record<string, ColumnStats>;
}

/** Local type for auto-stats column types */
type AutoStatsColumnType = 'numeric' | 'string' | 'date' | 'mixed';

/**
 * Maps the shared {@link DatasetColumnType} to the auto-stats column kind.
 * Auto-stats uses its own local vocabulary ('date' instead of 'datetime',
 * 'mixed' instead of 'unknown') for historical stats-output compatibility,
 * so this translator bridges the two spaces.
 */
function mapToAutoStatsType(values: unknown[]): AutoStatsColumnType {
  const sharedType = inferColumnType(values, { threshold: 0.9 });

  // Map shared types to auto-stats types
  switch (sharedType) {
    case 'number':
      return 'numeric';
    case 'datetime':
      return 'date';
    case 'string':
      return 'string';
    default:
      return 'mixed';
  }
}

/**
 * Extract numeric values from array, filtering out non-numbers and NaN.
 */
function extractNumbers(values: unknown[]): number[] {
  return values.filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
}

/**
 * Extract string values from array, filtering out non-strings.
 */
function extractStrings(values: unknown[]): string[] {
  return values.filter((v): v is string => typeof v === 'string');
}

/**
 * Compute statistics for a numeric column.
 */
function computeNumericStats(values: unknown[]): NumericColumnStats {
  const numbers = extractNumbers(values);
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  if (numbers.length === 0) {
    // No real numbers to aggregate — return `null` for every summary
    // statistic. Fabricating zeros here used to silently nudge the LLM
    // into treating an empty column as "all measurements were zero",
    // which is the autoStats half of the null-values-produce-sum-of-0
    // production bug.
    return {
      type: 'numeric',
      count: 0,
      nullCount,
      min: null,
      max: null,
      mean: null,
      median: null,
      stdev: null,
      p25: null,
      p75: null,
    };
  }

  return {
    type: 'numeric',
    count: numbers.length,
    nullCount,
    min: Math.min(...numbers),
    max: Math.max(...numbers),
    mean: mean(numbers),
    median: median(numbers),
    stdev: numbers.length > 1 ? standardDeviation(numbers) : 0,
    p25: quantile(numbers, 0.25),
    p75: quantile(numbers, 0.75),
  };
}

/**
 * Compute statistics for a string column.
 */
function computeStringStats(values: unknown[]): StringColumnStats {
  const strings = extractStrings(values);
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  return {
    type: 'string',
    count: strings.length,
    nullCount,
    distinctCount: new Set(strings).size,
  };
}

/**
 * Compute statistics for a date column.
 */
function computeDateStats(values: unknown[]): DateColumnStats {
  const dates = extractStrings(values).filter((s) => ISO_DATE_REGEX_BASIC.test(s));
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  if (dates.length === 0) {
    return {
      type: 'date',
      count: 0,
      nullCount,
      min: '',
      max: '',
    };
  }

  // Sort dates lexicographically (ISO format sorts correctly)
  const sortedDates = [...dates].sort();

  return {
    type: 'date',
    count: dates.length,
    nullCount,
    min: sortedDates[0] ?? '',
    max: sortedDates.at(-1) ?? '',
  };
}

/**
 * Compute statistics for a mixed column.
 */
function computeMixedStats(values: unknown[]): MixedColumnStats {
  const nullCount = values.filter((v) => v === null || v === undefined).length;

  return {
    type: 'mixed',
    count: values.length - nullCount,
    nullCount,
  };
}

/**
 * Compute auto-statistics for all columns in a dataset.
 *
 * Automatically detects column types using 90% threshold and computes
 * type-appropriate statistics:
 * - Numeric: min, max, mean, median, stdev, p25, p75
 * - String: distinct count
 * - Date: min, max as ISO strings
 * - Mixed: count only
 *
 * @param data - Array of records to analyze
 * @returns Statistics for each column
 */
export function computeAutoStats(data: Record<string, unknown>[]): AutoStatsResult {
  if (data.length === 0) {
    return { rowCount: 0, columns: {} };
  }

  const firstRecord = data[0];
  if (!firstRecord) {
    return { rowCount: 0, columns: {} };
  }

  const columnNames = Object.keys(firstRecord);
  const columns: Record<string, ColumnStats> = {};

  for (const colName of columnNames) {
    const values = data.map((row) => row[colName]);
    const colType = mapToAutoStatsType(values);

    // biome-ignore lint/nursery/noUnnecessaryConditions: False positive - switch checks enum value, not truthiness
    switch (colType) {
      case 'numeric':
        columns[colName] = computeNumericStats(values);
        break;
      case 'string':
        columns[colName] = computeStringStats(values);
        break;
      case 'date':
        columns[colName] = computeDateStats(values);
        break;
      case 'mixed':
        columns[colName] = computeMixedStats(values);
        break;
    }
  }

  return {
    rowCount: data.length,
    columns,
  };
}
